import type { ApiClient } from "./client.js";
import type { AccountRole, WireAccount } from "./types.js";

export function login(client: ApiClient, email: string, password: string): Promise<{ account: WireAccount }> {
  return client.post<{ account: WireAccount }>("/api/auth/login", { email, password });
}

export function logout(client: ApiClient): Promise<void> {
  return client.post<void>("/api/auth/logout");
}

/** `GET /api/auth/me` — throws (401, `ApiRequestError`) if there is no valid session; callers treat that as "anonymous", not an error to surface. */
export function getMe(client: ApiClient): Promise<{ account: WireAccount }> {
  return client.get<{ account: WireAccount }>("/api/auth/me");
}

/**
 * `POST /api/auth/lan-unlock` — exchanges the shared LAN passphrase for a
 * session cookie on a desktop deployment. Throws (`ApiRequestError`) on a
 * wrong passphrase (401) or once rate-limited (429); the unlock screen turns
 * those into a message. No-op shape (`void`) — the server replies 204 and the
 * cookie is what matters.
 */
export function lanUnlock(client: ApiClient, passphrase: string): Promise<void> {
  return client.post<void>("/api/auth/lan-unlock", { passphrase });
}

export function changePassword(client: ApiClient, currentPassword: string, newPassword: string): Promise<{ account: WireAccount }> {
  return client.post<{ account: WireAccount }>("/api/auth/change-password", { currentPassword, newPassword });
}

export function listAccounts(client: ApiClient): Promise<{ accounts: WireAccount[] }> {
  return client.get<{ accounts: WireAccount[] }>("/api/accounts");
}

export interface NewAccountBody {
  email: string;
  password: string;
  role: AccountRole;
}

export function createAccount(client: ApiClient, body: NewAccountBody): Promise<{ account: WireAccount }> {
  return client.post<{ account: WireAccount }>("/api/accounts", body);
}

export interface AccountPatchBody {
  role?: AccountRole;
  active?: boolean;
  password?: string;
}

export function updateAccount(client: ApiClient, id: string, patch: AccountPatchBody): Promise<{ account: WireAccount }> {
  return client.patch<{ account: WireAccount }>(`/api/accounts/${id}`, patch);
}
